import { createSlice } from "@reduxjs/toolkit";
import {
  getHandoffs,
  createHandoff,
  getHandoffDetail,
  scanHandoffItem,
  confirmSubstitution,
  completeHandoff,
  deliverHandoff,
  getHandoffSummary,
} from "./handoffActions";

const initialState = {
  loading: false,
  error: null,
  success: false,
  handoffs: {},
  activeHandoff: {},
  lastScanResult: null,
  substitutionPending: null,
  substituteLoading: false,
  completeLoading: false,
  lastSubstitutionResult: null,
};

const handoffSlice = createSlice({
  name: "handoff",
  initialState,
  reducers: {
    resetError: (state, action) => {
      if (action?.payload) {
        delete state.error[action?.payload];
        if (Object.keys(state.error)?.length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    resetLastScanResult: (state) => {
      state.lastScanResult = null;
    },
    clearSubstitutionPending: (state) => {
      state.substitutionPending = null;
    },
    clearActiveHandoff: (state) => {
      state.activeHandoff = {};
    },
    clearLastSubstitutionResult: (state) => {
      state.lastSubstitutionResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getHandoffs
      .addCase(getHandoffs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHandoffs.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.handoffs = payload;
        state.error = null;
      })
      .addCase(getHandoffs.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // createHandoff
      .addCase(createHandoff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createHandoff.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        state.handoffs = {
          meta: state?.handoffs?.meta,
          results: [payload, ...(state?.handoffs?.results || [])],
        };
        state.error = null;
      })
      .addCase(createHandoff.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      })
      // getHandoffDetail
      .addCase(getHandoffDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHandoffDetail.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeHandoff = payload;
        state.error = null;
      })
      .addCase(getHandoffDetail.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // scanHandoffItem
      .addCase(scanHandoffItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scanHandoffItem.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.lastScanResult = payload;
        state.error = null;
        // If the scan result indicates a mismatch, set substitutionPending
        if (payload?.status === "mismatch") {
          state.substitutionPending = payload;
        }
      })
      .addCase(scanHandoffItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // confirmSubstitution
      .addCase(confirmSubstitution.pending, (state) => {
        state.substituteLoading = true;
        state.error = null;
      })
      .addCase(confirmSubstitution.fulfilled, (state, { payload }) => {
        state.substituteLoading = false;
        state.substitutionPending = null;
        state.lastSubstitutionResult = payload;
        state.error = null;
      })
      .addCase(confirmSubstitution.rejected, (state, { payload }) => {
        state.substituteLoading = false;
        state.error = payload;
      })
      // completeHandoff
      .addCase(completeHandoff.pending, (state) => {
        state.completeLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(completeHandoff.fulfilled, (state, { payload }) => {
        state.completeLoading = false;
        state.activeHandoff = payload;
        state.success = true;
        state.error = null;
      })
      .addCase(completeHandoff.rejected, (state, { payload }) => {
        state.completeLoading = false;
        state.error = payload;
        state.success = false;
      })
      // deliverHandoff
      .addCase(deliverHandoff.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deliverHandoff.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeHandoff = payload;
        state.success = true;
        state.error = null;
      })
      .addCase(deliverHandoff.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      })
      // getHandoffSummary
      .addCase(getHandoffSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getHandoffSummary.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeHandoff = { ...state.activeHandoff, summary: payload };
        state.error = null;
      })
      .addCase(getHandoffSummary.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export const {
  resetError,
  resetSuccess,
  resetLastScanResult,
  clearSubstitutionPending,
  clearActiveHandoff,
  clearLastSubstitutionResult,
} = handoffSlice.actions;
export default handoffSlice.reducer;
